import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InventoryLayout from './InventoryLayout';

const OrderNew = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [items, setItems] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const loadData = async () => {
      try {
        const [partnersRes, itemsRes] = await Promise.all([
          fetch('/api/b2b/partners', { headers }),
          fetch('/api/inventory/items', { headers })
        ]);
        const partnersData = await partnersRes.json();
        const itemsData = await itemsRes.json();
        if (partnersRes.ok) setPartners(Array.isArray(partnersData) ? partnersData : []);
        if (itemsRes.ok) setItems(Array.isArray(itemsData) ? itemsData : []);
      } catch (_) {
        setPartners([]);
        setItems([]);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addLine = () => {
    setLines((prev) => [...prev, { item_id: '', qty: 1, unit_price: 0 }]);
  };

  const updateLine = async (index, field, value) => {
    setLines((prev) => prev.map((line, idx) => (idx === index ? { ...line, [field]: value } : line)));
    if (field === 'item_id' && value) {
      try {
        const res = await fetch(`/api/inventory/items/${value}`, { headers });
        const data = await res.json();
        if (res.ok) {
          const partnerPrice = (data.partner_prices || []).find((p) => String(p.partner_id) === String(partnerId));
          const price = partnerPrice ? partnerPrice.price : data.item.default_price;
          setLines((prev) =>
            prev.map((line, idx) => (idx === index ? { ...line, unit_price: price || 0 } : line))
          );
        }
      } catch (_) {}
    }
  };

  const removeLine = (index) => {
    setLines((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ partner_id: partnerId, reference, lines })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke oprette ordre');
      navigate(`/indkoeb/ordrer/${data.id}`);
    } catch (err) {
      setError(err.message || 'Kunne ikke oprette ordre');
    } finally {
      setSaving(false);
    }
  };

  return (
    <InventoryLayout>
      <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Ny ordre</h2>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Partner *</label>
            <select
              required
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
            >
              <option value="">Vælg partner</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>{partner.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Reference</label>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">Varelinjer</h2>
            <button type="button" onClick={addLine} className="text-sm text-falck-blue hover:underline">
              Tilføj linje
            </button>
          </div>
          {lines.length === 0 ? (
            <p className="text-sm text-gray-500">Ingen linjer endnu.</p>
          ) : (
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <select
                    value={line.item_id}
                    onChange={(e) => updateLine(index, 'item_id', e.target.value)}
                    className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                  >
                    <option value="">Vælg vare</option>
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={line.qty}
                    onChange={(e) => updateLine(index, 'qty', Number(e.target.value))}
                    className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={line.unit_price}
                    onChange={(e) => updateLine(index, 'unit_price', Number(e.target.value))}
                    className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                  />
                  <button type="button" onClick={() => removeLine(index)} className="text-red-600 text-sm">
                    Fjern
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate('/indkoeb/ordrer')} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md">
            Annuller
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-falck-blue text-white rounded-md disabled:opacity-60">
            {saving ? 'Gemmer...' : 'Opret ordre'}
          </button>
        </div>
      </form>
    </InventoryLayout>
  );
};

export default OrderNew;
